import type { CircuitPathResult } from "../../../ReviewPage/hooks/useCircuitCablePaths/types";
import type { WiringBasisOf, WiringUnit } from "./types";

const pointKey = ({ x, y }: { x: number; y: number }): string => `${x.toFixed(2)},${y.toFixed(2)}`;

const isControl = (role: string | null) => role === "control" || role === "twoWay";

// Read off the cable as drawn, not re-derived: what the card explains is what the metres measured.
export const wiringBasisOf: WiringBasisOf = ({ paths, roleOf, nameOf }) => {
  const deviceAt = new Map<string, CircuitPathResult>();
  paths.forEach((path) => {
    const end = path.waypoints[path.waypoints.length - 1];
    if (end) deviceAt.set(pointKey(end), path);
  });

  const units = new Map<string, WiringUnit>();
  const inUnits = new Set<string>();
  paths.forEach((path) => {
    const role = roleOf(path);
    if (!role || isControl(role)) return;
    const controls = path.waypoints
      .slice(1, -1)
      .map((point) => deviceAt.get(pointKey(point)))
      .filter((passed): passed is CircuitPathResult => Boolean(passed) && isControl(roleOf(passed!)));
    const out = controls[controls.length - 1];
    if (!out) return;
    const via = controls.length > 1 ? controls[controls.length - 2] : null;
    const unit = units.get(out.destinationId) ?? {
      control: nameOf(out),
      via: via ? nameOf(via) : null,
      lights: 0,
      loads: [],
    };
    if (role === "switchedLoad") unit.loads.push(nameOf(path));
    else unit.lights += 1;
    units.set(out.destinationId, unit);
    [path, out, ...(via ? [via] : [])].forEach((one) => inUnits.add(one.destinationId));
  });

  return {
    units: [...units.values()],
    directCount: paths.filter((path) => !inUnits.has(path.destinationId)).length,
  };
};
